/**
 * Boxes as printed on ICS 220-CG (Jan 2026), Air Operations Summary.
 *
 * The sheet runs to two printed pages of form content. Boxes 11 and 13 are the
 * same Prepared By reprinted at the foot of each, so the card shows it once
 * under a label naming both, as the 202 does for its 10 and 14.
 */
export const ICS220_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  operationalPeriod: '3. Operational Period (Date/Time):',
  astronomicalData: '4. Astronomical Data:',
  remarks:
    '5. Remarks: (Safety Notes, Hazards, Air Ops Special Instructions, etc)',
  readyAircraft: '6. Ready Aircraft:',
  tfrInfo: '7. Temporary Flight Restriction Info:',
  personnel: '8. Personnel:',
  frequencies: '9. Frequencies:',
  assignedAircraft: '10. Assigned Aircraft:',
  assignments: '12. Assignments:',
  preparedBy: '11 & 13. Prepared By: (Air Operations Branch Director)',
} as const;

/** Box 4 blanks. */
export const ICS220_ASTRONOMICAL_FIELDS = [
  { key: 'sunrise', label: 'Sunrise' },
  { key: 'sunset', label: 'Sunset' },
] as const;

/** Box 6 blanks, in printed order. */
export const ICS220_READY_AIRCRAFT_FIELDS = [
  { key: 'medevac', label: 'MEDEVAC' },
  { key: 'sar', label: 'SAR' },
  { key: 'le_security', label: 'LE/Security' },
  { key: 'new_incident', label: 'New Incident' },
] as const;

/** Box 7 blanks. */
export const ICS220_TFR_FIELDS = [
  { key: 'tfr_number', label: 'Number' },
  { key: 'tfr_altitude', label: 'Altitude' },
] as const;

/** Box 8 rows: the air operations seats the sheet names, with a phone blank. */
export const ICS220_PERSONNEL_ROWS = [
  { key: 'airOperationsBranchDirector', label: 'Air Operations Branch Director' },
  { key: 'airSupportGroupSupervisor', label: 'Air Support Group Supervisor' },
  { key: 'airTacticalGroupSupervisor', label: 'Air Tactical Group Supervisor' },
  { key: 'rotaryWingCoordinator', label: 'Rotary-Wing Coordinator' },
  { key: 'fixedWingCoordinator', label: 'Fixed-Wing Coordinator' },
  { key: 'airfieldCoordinator', label: 'Airfield Coordinator/Manager' },
  { key: 'maintenanceCoordinator', label: 'Maintenance Coordinator' },
] as const;

/** Box 9 rows, each with a Primary and a Secondary blank. */
export const ICS220_FREQUENCY_ROWS = [
  { key: 'airAirFixedWing', label: 'Air/Air Fixed-Wing' },
  {
    key: 'airAirRotaryWing',
    label: 'Air/Air Rotary Wing – Flight Following',
  },
  { key: 'airGround', label: 'Air/Ground' },
  { key: 'command', label: 'Command' },
  { key: 'deckCoordinator', label: 'Deck Coordinator' },
  { key: 'takeOffLandingCoordinator', label: 'Take-off & Landing Coordinator' },
  { key: 'airGuard', label: 'Air Guard' },
] as const;

/** Columns of box 10. */
export const ICS220_AIRCRAFT_COLUMNS = [
  { key: 'kind', label: 'Helo / Fixed-Wing' },
  { key: 'tail_number', label: 'CG Tail # or FAA N#' },
  { key: 'model', label: 'Model' },
  { key: 'home_base', label: 'Home Base' },
  { key: 'status', label: 'Status' },
  { key: 'available', label: 'Time Available' },
  { key: 'commence', label: 'Time Commence' },
  { key: 'remarks', label: 'Remarks' },
] as const;

/** Columns of box 12. */
export const ICS220_SORTIE_COLUMNS = [
  { key: 'sortie_number', label: 'Sortie #' },
  { key: 'tail_number', label: 'Tail # / FAA N#' },
  { key: 'aircraft_type', label: 'Aircraft Type' },
  { key: 'mission', label: 'Mission / Purpose' },
  { key: 'location', label: 'Location / Area' },
  { key: 'start_time', label: 'Start Time' },
  { key: 'end_time', label: 'End Time' },
  { key: 'total_flight_time', label: 'Total Flight Time' },
  { key: 'remarks', label: 'Remarks/Results' },
] as const;

/** The two kinds box 10 offers as a pair of ticks. */
export const ICS220_AIRCRAFT_KINDS = ['Helo', 'Fixed-Wing'] as const;
